import { useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import classNames from 'classnames'
import Link from 'components/misc/Link'
import Header from 'components/typography/Header'
import { toggleModal } from 'store/ui/actions'
import Button from 'components/forms/Button'
import * as analytics from 'lib/analytics'
import { useDispatch, useSelector } from 'react-redux'
import { setMode } from 'store/login-modal/actions'
import Endorsements from 'components/pricing/Endorsements'
import BlurOver from 'components/misc/BlurOver'
import { userIsAtLeast } from 'lib/user'

import styles from './styles/PaywallBanner.css'
import { PLANS } from '../../../server/lib/constants'

const renderPaywallBlurb = ({
  paywallSource,
  header,
  user
}) => {
  if (paywallSource === 'Analysis') {
    return (
      <div className={styles.offering}>
        <Header level={5}>Get access to an organizational plan to view the remaining information in this executive summary</Header>
        <Link href='https://cdn.scite.ai/sample_files/scite_pdf_analysis_example.pdf' blue underline target='_blank' rel='noreferrer'>
          View Example
        </Link>
      </div>
    )
  }

  if (paywallSource === 'Dashboard') {
    return (
      <div className={styles.offering}>
        <Header level={5}>Get access to an organizational plan to view the remaining information in this dashboard, including:</Header>
        <ul className={styles.valueProps}>
          <li>Trends in the yearly scite Index for these publications</li>
          <li>List of authors who contributed to publications in this dashboard to help you find reviewers, collaborators, or thought-leaders</li>
          <li>Searchable, filterable table of publications along with their scite classification tallies</li>
          <li>Configurable alerts to stay be notified as new citations happen to these publications</li>
        </ul>
        <Header level={5}>You will also get access to:</Header>
        <ul className={styles.valueProps}>
          <li>Assistant by scite, a conversational tool like ChatGPT with guardrails for real, up to date references</li>
          <li>Citation Statement Search to find publications that support or contrast your research</li>
          <li>Citation reports and visualizations to easily read what publications are saying about each other</li>
          <li>Browser extension to see Smart Citations wherever you read research</li>
          <li>Automated reference checks to make sure you are citing reliable research in your manuscripts</li>
          <li><strong>{CONFIG.trialLength} day free preview of our premium features.</strong></li>
        </ul>
      </div>
    )
  }

  if (paywallSource === 'Author Profile' && !user) {
    return (
      <div className={styles.offering}>
        <Header level={5}>Create an account to access the remaining information in this Researcher Profile:</Header>
        <ul className={styles.valueProps}>
          <li>Their last known affiliation</li>
          <li>Sortable list of their publications enriched with scite Smart Citation tallies</li>
        </ul>
        <Header level={5}>You will also get access to:</Header>
        <ul className={styles.valueProps}>
          <li>Assistant by scite, a conversational tool like ChatGPT with guardrails for real, up to date references</li>
          <li>Citation Statement Search to find publications that support or contrast your research</li>
          <li>Citation reports and visualizations to easily see what publications are saying about each other</li>
          <li>Browser extension to see Smart Citations wherever you read research</li>
          <li>Automated reference checks to make sure you are citing reliable research in your manuscripts</li>
          <li><strong>{CONFIG.trialLength} day free preview of our premium features.</strong></li>
        </ul>
      </div>
    )
  }

  if (paywallSource === 'Author Profile' && !userIsAtLeast(user, PLANS.premium)) {
    return (
      <div className={styles.offering}>
        <Header level={5}>Upgrade to our premium plan to access the remaining information in this Researcher Profile:</Header>
        <ul className={styles.valueProps}>
          <li>Their last known affiliation</li>
          <li>Sortable list of their publications enriched with scite Smart Citation tallies</li>
        </ul>
        <Header level={5}>With a premium account, you also get access to:</Header>
        <ul className={styles.valueProps}>
          <li>Assistant by scite, a conversational tool like ChatGPT with guardrails for real, up to date references</li>
          <li>Citation Statement Search to help you discover reliable research and experts in the field</li>
          <li>Unlimited custom dashboards with up to 1,000 DOIs each</li>
          <li>Smart Citation email alerts to be notified when new citation statements are made to research you care about</li>
          <li>... and much more!</li>
        </ul>
      </div>
    )
  }

  if (paywallSource === 'Citation Statement Search' && !user) {
    return (
      <div className={styles.offering}>
        <Header level={5}>{header}</Header>
        <ul className={styles.valueProps}>
          <li>Assistant by scite, a conversational tool like ChatGPT with guardrails for real, up to date references</li>
          <li>Search over {CONFIG.defaultCitationsTotalText}+ citation statments to see what is being said about any topic in the research literature</li>
          <li>Citation Statement Search to find publications that support or contrast your research</li>
          <li>Unlimited citation reports and visualizations that show what publications are saying about each other</li>
          <li>Browser extension to see Smart Citations wherever you read research</li>
          <li>Dashboards to evaluate and keep track of groups of publications</li>
          <li>Alerts to stay on top of citations as they happen</li>
          <li>Automated reference checks to make sure you are citing reliable research in your manuscripts</li>
          <li><strong>{CONFIG.trialLength} day free preview of our premium features.</strong></li>
        </ul>
      </div>
    )
  }

  if (paywallSource === 'Citation Statement Search' && user) {
    return (
      <div className={styles.offering}>
        <Header level={5}>{header}</Header>
        <ul className={styles.valueProps}>
          <li>Search over {CONFIG.defaultCitationsTotalText}+ citation statments to see what is being said about any topic in the research literature</li>
          <li>Assistant by scite, a conversational tool like ChatGPT with guardrails for real, up to date references</li>
          <li>Unlimited scite reports and visualizations that show what publications are saying about each other</li>
          <li>Dashboards to monitor the publication impact of groups of papers</li>
          <li>Alerts to stay on top of citations as they happen</li>
          <li>Automated reference checks to make sure you are citing reliable research in your manuscripts</li>
        </ul>
      </div>
    )
  }

  return (
    <div className={styles.offering}>
      <Header level={5}>{header}</Header>
      <ul className={styles.valueProps}>
        <li>Assistant by scite, a conversational tool like ChatGPT with guardrails for real, up to date references</li>
        <li>Search over {CONFIG.defaultCitationsTotalText}+ citation statments to see what is being said about any topic in the research literature</li>
        {!user && <li>Citation reports and visualizations to easily see what publications are saying about each other</li>}
        {user && <li>Unlimited citation reports and visualizations that show what publications are saying about each other</li>}
        {!user && <li>Browser extension to see Smart Citations wherever you read research</li>}
        <li>Dashboards to evaluate and keep track of groups of publications</li>
        <li>Alerts to stay on top of citations as they happen</li>
        <li>Automated reference checks to make sure you are citing reliable research in your manuscripts</li>
        {!user && <li><strong>{CONFIG.trialLength} day free preview of our premium features.</strong></li>}
        {user && <li>Plans starting at $12/month <Link blue underline to='/pricing'>(see pricing)</Link></li>}
      </ul>
    </div>
  )
}

const Cta = ({ user }) => {
  if (user) {
    return (
      <>
        {user?.activatedTrial ? 'Upgrade Now' : 'Activate Trial'} <i className={`icon icon-right-arrow ${styles.arrowImg}`} />
      </>
    )
  }

  return (
    <>Sign up for free <i className={`icon icon-right-arrow ${styles.arrowImg}`} /></>
  )
}

const CtaButton = ({ orgOnly, user, ctaAction, dispatch }) => {
  const history = useHistory()

  if (orgOnly) {
    return <Link href='mailto:sales@scite.ai' blue underline bold>Contact Us</Link>
  }

  return (
    <Button
      className={styles.paywallRegisterBtn}
      onClick={() => {
        ctaAction && ctaAction()
        if (user) {
          history.push('/pricing')
        } else {
          dispatch(setMode('register'))
          dispatch(toggleModal('login'))
        }
      }}
    >
      <Cta user={user} orgOnly={orgOnly} />
    </Button>
  )
}

export const PaywallBanner = ({
  className,
  freeHeader,
  upgradeHeader,
  paywallSource,
  children,
  show = true,
  orgOnly = false,
  ctaAction // a custom action that get's passed to onClick and gets called in the CTA
}) => {
  const dispatch = useDispatch()
  const user = useSelector(({ ui }) => ui.user)

  const header = user ? upgradeHeader : freeHeader

  useEffect(() => {
    analytics.event({
      category: 'Paywall',
      action: paywallSource,
      label: user ? 'View Upgrade Paywall' : 'View Sign Up Paywall'
    })
  }, [])

  return (
    <BlurOver
      className={classNames(styles.paywallBannerContainer, className)}
      hoverEffect
      bgStyle={{ show: { top: '1rem' } }}
      show={show}
      foreground={() => (
        <div className={styles.fgWrapper}>
          {renderPaywallBlurb({
            paywallSource,
            header,
            user
          })}
          <CtaButton orgOnly={orgOnly} user={user} ctaAction={ctaAction} dispatch={dispatch} />
          <Endorsements />
        </div>
      )}
    >
      {children}
    </BlurOver>
  )
}

export default PaywallBanner
